import { AnyAction } from 'redux';

// Data layer
import {
  QuotationSummaryResponse,
  QuotationUnit,
  ServerResponse,
} from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';

import { getAllCoverages } from 'data-layer-cr/dist/functions/quotations';
import { QuotationData } from 'data-layer-cr/dist/responseObjects';

// Redux
import { getLoggedRole } from 'data-layer-cr/dist/authentication';
import { AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

// Network
import processRequest from '../../../services/processRequest';

// Actions
import { updateTotal as updateSearchTotal } from '../../infiniteScroll/slice';
import { updateLastQuotationPolicy } from '../../notification/slice';
import {
  overwriteCurrentQuotation,
  updateCarItemFromLicense,
  updateCurrentProfile,
  updateCurrentQuotationChanges,
  updateQuotationSummaryList,
  updateSavedQuotationUnit,
  updateSubagents,
} from '../slice/quotationSlice';

import {
  GET_DETAILS,
  GET_DETAILS_CHANGES,
  GET_LIST,
  REQUEST_LICENSEPLATE,
  REQUEST_PRICING_INITIAL,
  REQUEST_SUBAGENTS,
} from '../requestIdentifiers';

// Request factory methods
import { makePricingPreviewRequest } from '../../../models/requestTypes/RequestFactory';

// Methods
import {
  hasUnits,
  makeBlankQuotation,
} from '../../../functions/quotationMethods';
import {
  appliesAutoPxSpecialPlans,
  appliesNewAutoPxDiscount,
  requestQuotationPricingByProfile,
  sanitizeCarDetails,
  updateQuotationUnits,
} from './helperMethods';

// Models
import QuotationChange from '../../../models/Quotation/QuotationChange';
import CarItem from '../../../models/helperTypes/CarItem';
import CustomerItem from '../../../models/helperTypes/CustomerItem';

// Request/Response types
import CarItemFromLicense, {
  makeEmpty,
} from '../../../models/Quotation/CarItemFromLicense';
import SubAgentItem from '../../../models/Quotation/SubAgentItem';
import PricingPreviewRequest from '../../../models/requestTypes/PricingPreviewRequest';
import PricingProfileResponse from '../../../models/responseTypes/PricingProfileResponse';
import { PAGINATION_STEP_SIZE } from './makeUpdateActions';

const quotationPricePerUnitEndpoint = 'Quotation/priceperunit';

/**
 * The first quotation request, updates the current quotation data with all
 * data from the server.
 *
 * @param car - The car data
 * @param driver - The driver data
 * @param usedLicense - Which license was selected from the dropdown
 */
export const requestQuotationUpdate =
  (car: CarItem, driver: CustomerItem, reset = false): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    let quotation = getState().quotation.currentQuotation;
    const { currentProfile } = getState().quotation;
    const { isLicenseAutoPx, usedProfile } = getState().quotation;

    if (!hasUnits(quotation)) {
      quotation = makeBlankQuotation(quotation.isAutoPx, car, driver);
    }

    // First, create the request data
    const requestData: PricingPreviewRequest = makePricingPreviewRequest(
      driver,
      car,
      currentProfile.productId,
      reset ? [] : getAllCoverages(quotation),
      undefined,
      usedProfile,
      true,
    );
    // Perform requests
    dispatch(setRequestStatus(REQUEST_PRICING_INITIAL, 'requesting'));
    try {
      const respQuotation = await requestQuotationPricingByProfile(
        requestData,
        currentProfile,
      );

      /** Validates by license and profile */
      const hasAutoPx = appliesAutoPxSpecialPlans(
        isLicenseAutoPx,
        respQuotation.quotationProfile,
        getLoggedRole(),
      );

      /** Data from the previous quotation */
      respQuotation.customer = quotation.customer;
      respQuotation.summary = quotation.summary;
      respQuotation.isAutoPx = hasAutoPx;
      respQuotation.user = {
        ...quotation.user,
        role: getLoggedRole(),
      };

      respQuotation.hasMultipleOptions = true;

      /** This endpoint will be refactored later (sprint 68),
       *  it won't be needing this condition since it will always
       *  be called
       */

      respQuotation.units[0].unit.isNewCar = requestData.units[0].Unit.isNewCar;

      /** No package selected */
      respQuotation.packageOption = -1;

      respQuotation.units[0].unit.modelGroupId = car.model.groupId as
        | string
        | undefined;

      const sortedUnits = [...respQuotation.units].sort((unitA, unitB) =>
        unitA.package.order > unitB.package.order ? 1 : -1,
      );

      respQuotation.units = sortedUnits;
      dispatch(updateSavedQuotationUnit(respQuotation.units));
      dispatch(overwriteCurrentQuotation(respQuotation));
      return dispatch(setRequestStatus(REQUEST_PRICING_INITIAL, 'success'));
    } catch {
      return dispatch(setRequestStatus(REQUEST_PRICING_INITIAL, 'failure'));
    }
  };

export function getHigherQuotationPolicyNumber(
  list: QuotationSummaryResponse[],
): number {
  const emitedList = list.filter((qs) => qs.status.id === 4);
  return emitedList.length === 0 ? -1 : emitedList[0].id;
}

/**
 * Loads a list of the quotations created by the current user. Note that
 * the entity used for list entries is `QuotationSummaryResponse`, not `Quotation`
 *
 * @param searchString - If provided, a filter will be applied to the response
 */
export const getQuotations =
  ({
    searchString = '',
    additionalFilters = '',
    skip = 0,
    step = PAGINATION_STEP_SIZE,
  }: {
    searchString?: string;
    additionalFilters?: string;
    skip?: number;
    step?: number;
    test?: string;
  } = {}): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const { lastQuotationPolicy } = getState().notification;
    dispatch(setRequestStatus(GET_LIST, 'requesting'));
    try {
      const searchQueryString =
        searchString !== '' ? `&filter[search]=${searchString}` : '';
      const filterQueryStrings =
        additionalFilters !== '' ? `&${additionalFilters}` : '';
      const response = await processRequest<
        NetworkResponse<QuotationSummaryResponse[]>
      >({
        method: 'GET',
        url: `Quotation/summaries?filter[skip]=${skip}&filter[limit]=${step}${searchQueryString}${filterQueryStrings}`,
        uniqueIdentifier: GET_LIST,
      });

      // Pagination information is needed to handle infinite scroll
      if (response.pagination === undefined) {
        throw new Error();
      }

      // Getting the las quotation policy number
      const newLastQuotationPolicy = Math.max(
        lastQuotationPolicy,
        getHigherQuotationPolicyNumber(response.data),
      );
      dispatch(updateLastQuotationPolicy(newLastQuotationPolicy));
      dispatch(updateQuotationSummaryList(response.data));
      dispatch(updateSearchTotal(response.pagination.total));
      return dispatch(setRequestStatus(GET_LIST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_LIST, 'failure'));
    }
  };

/**
 * Loads the quotation identifier by `quotationId` to the store.
 *
 * @param quotationId - The id for the specific quotation to load
 */
export const getById =
  (hashQuotation: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_DETAILS, 'requesting'));
    try {
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `quotation?hash=${hashQuotation}`,
      });

      /** This is a temporary solution, on sprint 68 it will be refactored
       * so this endpoint would be called every time.
       */
      const { quotation } = response.data;

      quotation.units.sort((unitA, unitB) =>
        unitA.package.order > unitB.package.order ? 1 : -1,
      );

      const appliesForNewAutoPxDiscount = appliesNewAutoPxDiscount(
        response.data.quotation.isAutoPx,
      );

      if (appliesForNewAutoPxDiscount) {
        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: quotationPricePerUnitEndpoint,
          data: quotation.units,
        });

        quotation.units = updateQuotationUnits(
          quotation.units,
          autoPxUnits.data,
        );
      }
      const usedProfileType = getState().pricingProfiles.profiles.find(
        (profile) => profile.id === quotation.quotationProfile.id,
      );
      dispatch(updateCurrentProfile(usedProfileType as PricingProfileResponse));
      dispatch(overwriteCurrentQuotation(response.data.quotation));
      return dispatch(setRequestStatus(GET_DETAILS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_DETAILS, 'failure'));
    }
  };

/**
 * Loads the history of quotation state changes related to the provided
 * quotation id
 *
 * @param quotationId - The source quotation for the changes
 */
export const getChangesForQuotationId =
  (quotationHash: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_DETAILS_CHANGES, 'requesting'));
    try {
      const data = await processRequest<QuotationChange[]>({
        method: 'GET',
        url: `Quotation/v2/quotationchanges?hash=${quotationHash}`,
      });
      dispatch(updateCurrentQuotationChanges(data));
      return dispatch(setRequestStatus(GET_DETAILS_CHANGES, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(GET_DETAILS_CHANGES, 'failure'));
    }
  };

/**
 * Requests the full object of the provided car License.
 *
 * @param licensePlate - The licensePlate to filter with
 * @param category - The license category to filter with
 */
export const getCarDetailsFromLicense =
  (category: string, licensePlate: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_LICENSEPLATE, 'requesting'));
    try {
      const data = await processRequest<CarItemFromLicense>({
        method: 'GET',
        url: `Quotation/GetVehicleDetailsByLicensePlate/${category}/${licensePlate}`,
      });
      const sanitizedData = sanitizeCarDetails(data);
      dispatch(updateCarItemFromLicense(sanitizedData));
      return dispatch(setRequestStatus(REQUEST_LICENSEPLATE, 'success'));
    } catch (e) {
      dispatch(updateCarItemFromLicense(makeEmpty()));
      return dispatch(setRequestStatus(REQUEST_LICENSEPLATE, 'failure'));
    }
  };

/**
 * Requests the list of intermeiaries for the provided Agency License.
 *
 * @param intermediaryLicense - Which license was selected from the dropdown
 */
export const getSubagents =
  (intermediaryLicense: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_SUBAGENTS, 'requesting'));
    try {
      const data = await processRequest<ServerResponse<SubAgentItem[]>>({
        method: 'GET',
        url: `Quotation/subagents/${intermediaryLicense}`,
      });
      dispatch(updateSubagents(data.data));
      return dispatch(setRequestStatus(REQUEST_SUBAGENTS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_SUBAGENTS, 'failure'));
    }
  };
